import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import * as BrowserHelper from '@Services/BrowserHelper';
import PrepareTrackingDataHelper from '@Services/PrepareTrackingDataHelper';
import { citationLiteratureFormatter, patentFamilyFormatter } from '@Services/FormatUtils';
import externalUrl from '@Utils/ExternalUrl';
import DocumentButtonsComponent from '@Components/Results/documents/document-buttons.component';
import ListOfAuthorsComponent from '@Components/Results/reactions/reaction-details/list-of-authors.component';
import DocumentCollapsibleContentComponent from '@Components/Results/documents/document-collapsible-content.component';
import DocumentJournalsComponent from '@Components/Results/documents/document-journals.component';
import { getValueByNs } from '@Hooks/LibraryInitialization/MoreUnderscore';
import { DOCUMENT_PROCESSING_TYPES } from '@Containers/ResultsFilters/ResultsFiltersConstants';
import Auth from '@Services/Auth';
import { FEATURES } from '@Constants/rx-ui/constants';
import { getDocumentNoAuthorLabel } from '@Components/Results/reactions/reaction-details/ReactionCitations.jsx';
import Catalyst from '@Services/Catalyst';
import { sectionsList, getFirstAvailableSection } from '@Components/Results/documents/DocumentExpandSections';

function DocumentComponent(props) {
  const { metadata, document: citation, zoom: zoomValue, context } = props;
  const [state, setState] = useState(() => {
    const expandFlags = sectionsList.reduce((acc, flag) => {
      acc[flag] = false;
      return acc;
    }, {});
    return { updatedCitedByUrl: '', ...expandFlags };
  });
  const isIE = useRef(BrowserHelper.detectIE());

  useEffect(() => {
    async function updateUrls() {
      if (citation.issueCitedByCounter && citation.issueCitedByCounter > 0) {
        const updatedCitedByUrl = await Catalyst.appendVisitorIDToUrl(externalUrl(citation.citedBy));
        setState((prev) => ({ ...prev, updatedCitedByUrl }));
      }
    }
    updateUrls();
  }, [citation.issueCitedByCounter, citation.citedBy]);

  const extractExpandFlagsFromState = useCallback(
    () =>
      sectionsList.reduce((acc, flag) => {
        acc[flag] = state[flag];
        return acc;
      }, {}),
    [state]
  );

  const isPatent = useCallback(() => /patent/gi.test(getValueByNs(props, 'document.type')), [props]);

  const getTrackingAttrs = useCallback(
    (trackingAttrs, prepare = true) => ({
      'data-click-delegation': 'true',
      'data-tracking': 'true',
      'data-tracking-context': context || get(metadata, 'context'),
      'data-tracking-preview': getValueByNs(props, 'metadata.isViewPreview'),
      'data-tracking-track-link-location-in-results': true,
      'data-tracking-from-rt': metadata.fromRT,
      ...trackingAttrs,
      ...(prepare ? PrepareTrackingDataHelper.prepare(citation) : {}),
    }),
    [context, metadata, citation, props]
  );

  const toggleSection = useCallback((event, section) => {
    if (event) {
      const target = event.currentTarget;
      event.preventDefault();
      setTimeout(() => {
        if (!DocumentComponent.checkIfClickTargetContainsSelection(target)) {
          setState((prev) => ({ ...prev, [section]: !prev[section] }));
        }
      }, 0);
    } else {
      setState((prev) => ({ ...prev, [section]: !prev[section] }));
    }
  }, []);

  const titleClickToggleSection = useCallback(
    (e) => {
      const section = getFirstAvailableSection(citation);
      if (section) {
        toggleSection(e, section);
      }
    },
    [citation, toggleSection]
  );

  const titleKeyPressToggleSection = useCallback(
    (e) => {
      if (e && e.key === 'Enter') {
        const section = getFirstAvailableSection(citation);
        if (section) {
          toggleSection(e, section);
        }
      }
    },
    [citation, toggleSection]
  );

  const { abstractExpanded } = state;
  const { updatedCitedByUrl } = state;
  const zoom = metadata.isViewPreview ? 0 : zoomValue;
  const processingMode =
    Auth.isFeatureEnabled(FEATURES.enableRccDocumentProcessedTypeLabel) && DOCUMENT_PROCESSING_TYPES.getType(citation.processingMode);

  return (
    <>
      <div className="rx-element-content">
        <div className="rx-element-info">
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
          <span
            className="rx-element-link e2e-document-action-title"
            role="button"
            tabIndex={citation.abstractText ? '0' : '-1'}
            onClick={titleClickToggleSection}
            {...(isIE.current ? { onKeyDown: titleKeyPressToggleSection } : { onKeyPress: titleKeyPressToggleSection })}
            {...getTrackingAttrs({
              'data-tracking': !abstractExpanded,
              'data-tracking-type': 'contentlinkclick',
              'data-tracking-action': 'title',
            })}>
            {citation.title ? (
              <InnerHTML tag="h2" className="rx-element-name" html={citation.title} />
            ) : (
              <span className="rx-element-name">No title</span>
            )}
          </span>
          {isPatent() && citation.assignees && (
            <>
              {!Auth.isFeatureEnabled(FEATURES.disablePatentSightUIChanges) && <span>Current Patent Assignee: </span>}
              <InnerHTML tag="span" className="rx-element-assignee" html={citation.assignees + (citation.authors ? '; ' : '')} />
            </>
          )}
          {(() => {
            let authorsContent;
            if (citation.authors) {
              authorsContent = <ListOfAuthorsComponent metadata={metadata} citation={citation} />;
            } else if (citation.assignees) {
              authorsContent = null;
            } else {
              authorsContent = getDocumentNoAuthorLabel(citation.year);
            }
            return <span className="rx-element-authors">{authorsContent}</span>;
          })()}{' '}
          {/* for patent */}
          {isPatent() && (
            <InnerHTML
              tag="span"
              className="rx-element-literature"
              html={`- ${DocumentComponent.literatureFormatter(citation.citationPatents)}`}
            />
          )}
          {!isEmpty(citation.citationJournals) && (
            <DocumentJournalsComponent journals={citation.citationJournals} doi={citation.doi} withDoiLink />
          )}
          {/* again for patent */}
          {isPatent() && citation.patentFamily && (
            <div className="rx-element-literature">
              <span className="rx-element-literature-title">Patent Family Members: </span>
              <InnerHTML tag="span" html={DocumentComponent.patentFormatter(citation.patentFamily)} />
            </div>
          )}
          <DocumentButtonsComponent
            document={citation}
            toggleSection={toggleSection}
            {...extractExpandFlagsFromState()}
            getTrackingAttrs={getTrackingAttrs}
            metadata={metadata}
          />
        </div>

        {(citation.issueCitedByCounter > 0 || citation.processingMode) && (
          <div className="rx-element-additional-tags">
            {processingMode && (
              <div className="rx-element-processing-mode e2e-processing-mode">
                <svg className="els-btn-icon rx-border-none els-btn-icon-sm processing-mode-icon">
                  <use xlinkHref={`#icon-${processingMode.icon}`} />
                </svg>
                {processingMode.label}
              </div>
            )}
            {citation.issueCitedByCounter > 0 && (
              <a
                className="els-btn els-btn-link blue-compact--underline els-btn-xs e2e-cited-count rx-element-cited-count"
                target="_blank"
                href={updatedCitedByUrl}
                {...getTrackingAttrs(
                  {
                    'data-tracking-type': 'linkout',
                    'data-tracking-action': 'cited',
                  },
                  true
                )}>
                Cited {citation.issueCitedByCounter} times
              </a>
            )}
          </div>
        )}
      </div>

      <DocumentCollapsibleContentComponent
        metadata={metadata}
        document={citation}
        zoom={zoom}
        toggleSection={toggleSection}
        {...extractExpandFlagsFromState()}
        getTrackingAttrs={getTrackingAttrs}
      />
    </>
  );
}

DocumentComponent.expandFlags = sectionsList;
DocumentComponent.literatureFormatter = function (content) {
  return citationLiteratureFormatter(content);
};
DocumentComponent.patentFormatter = function (content) {
  return patentFamilyFormatter(content);
};
DocumentComponent.checkIfClickTargetContainsSelection = function (target) {
  const selection = window.getSelection();
  const isSelected = !!selection.toString();
  const selectedNode = getValueByNs(selection, 'anchorNode.parentNode');
  return isSelected && selectedNode && target.contains(selectedNode);
};

DocumentComponent.propTypes = {
  metadata: MetadataPropTypesShape,
  document: PropTypes.object.isRequired,
  zoom: PropTypes.number,
  context: PropTypes.string,
};

export default DocumentComponent;
